import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { ItemCode } from '../../types';

export default function ProductsPage() {
  const [products, setProducts] = useState<ItemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [form, setForm] = useState({
    code: '',
    description: '',
    castor_type: '',
    wheel_type: '',
    wheel_material: '',
    size: '',
    bearing: '',
    circlip: '',
    bush: '',
    thread_guard: '',
    nut_bolt: '',
    base_price: '',
    is_active: true,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  async function fetchProducts() {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('item_codes')
        .select('*')
        .order('code', { ascending: true });

      if (error) {
        setError(error.message);
        return;
      }

      setProducts(data || []);
    } catch (err) {
      setError('Failed to fetch products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      code: '',
      description: '',
      castor_type: '',
      wheel_type: '',
      wheel_material: '',
      size: '',
      bearing: '',
      circlip: '',
      bush: '',
      thread_guard: '',
      nut_bolt: '',
      base_price: '',
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
  }

  function editProduct(product: ItemCode) {
    setForm({
      code: product.code,
      description: product.description || '',
      castor_type: product.castor_type || '',
      wheel_type: product.wheel_type || '',
      wheel_material: product.wheel_material || '',
      size: product.size || '',
      bearing: product.bearing || '',
      circlip: product.circlip || '',
      bush: product.bush || '',
      thread_guard: product.thread_guard || '',
      nut_bolt: product.nut_bolt || '',
      base_price: product.base_price ? product.base_price.toString() : '',
      is_active: product.is_active ?? true,
    });
    setEditingId(product.id || null);
    setShowForm(true);
  }

  async function deleteProduct(id: number) {
    if (!window.confirm('Are you sure you want to delete this product catalog item?')) return;
    try {
      const { error } = await supabase.from('item_codes').delete().eq('id', id);
      if (error) throw error;
      setProducts(products.filter((p) => p.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete product');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.code) return;

    try {
      const productData = {
        code: form.code,
        description: form.description || null,
        castor_type: form.castor_type || null,
        wheel_type: form.wheel_type || null,
        wheel_material: form.wheel_material || null,
        size: form.size || null,
        bearing: form.bearing || null,
        circlip: form.circlip || null,
        bush: form.bush || null,
        thread_guard: form.thread_guard || null,
        nut_bolt: form.nut_bolt || null,
        base_price: form.base_price ? parseFloat(form.base_price) : null,
        is_active: form.is_active,
      };

      if (editingId) {
        const { error } = await supabase.from('item_codes').update(productData).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('item_codes').insert([productData]);
        if (error) throw error;
      }

      resetForm();
      fetchProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to save product');
    }
  }

  const filteredProducts = products.filter((p) => {
    const q = searchQuery.toLowerCase();
    return (
      p.code.toLowerCase().includes(q) ||
      (p.description && p.description.toLowerCase().includes(q)) ||
      (p.wheel_material && p.wheel_material.toLowerCase().includes(q)) ||
      (p.size && p.size.toLowerCase().includes(q)) ||
      (p.bearing && p.bearing.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Finished Product Catalog</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Finished castor wheel assemblies and standard technical specifications ({products.length} master codes)
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <span>{showForm ? '✕' : '+'}</span>
          <span>{showForm ? 'Close Form' : 'Add New Product'}</span>
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs">
          {error}
        </div>
      )}

      {/* Add / Edit Form Card */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-elevated border border-slate-200/80 p-6 sm:p-7 space-y-4">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                {editingId ? 'Modify Assembly' : 'New Catalog Item'}
              </span>
              <h2 className="text-base font-bold text-slate-900">
                {editingId ? 'Edit Product Item' : 'Create New Product Item'}
              </h2>
            </div>
            <button
              onClick={resetForm}
              className="text-xs text-slate-400 hover:text-slate-600 font-bold p-1 rounded hover:bg-slate-100"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Product Code <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  placeholder="e.g. SF1/SW/PP/F/615"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Description</label>
                <input
                  placeholder="e.g. 150mm PP Swivel Castor"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Size / Dimension</label>
                <input
                  placeholder="e.g. 150 x 50 mm"
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Wheel Material</label>
                <input
                  placeholder="e.g. Polypropylene (PP), Nylon, PU"
                  value={form.wheel_material}
                  onChange={(e) => setForm({ ...form, wheel_material: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Castor / Bracket Type</label>
                <input
                  placeholder="e.g. Swivel, Fixed, Swivel Break"
                  value={form.castor_type}
                  onChange={(e) => setForm({ ...form, castor_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Base Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="850.00"
                  value={form.base_price}
                  onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono font-bold"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-slate-300"
              />
              <label htmlFor="is_active" className="text-xs font-bold text-slate-700 cursor-pointer">
                Product Active for Quoting
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-colors cursor-pointer"
              >
                {editingId ? 'Update Product' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table Container Card */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200/80 bg-slate-50/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-96 relative">
            <input
              type="text"
              placeholder="Search by product code, material, description, or size..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:border-blue-500 shadow-sm text-slate-900 placeholder-slate-400 transition-colors"
            />
            <span className="absolute left-3.5 top-3 text-slate-400 text-sm">🔍</span>
          </div>

          <div className="text-xs font-medium text-slate-500">
            Showing <strong className="text-slate-900">{filteredProducts.length}</strong> products
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-500 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
            <span className="text-xs font-semibold">Loading product catalog...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-16 text-center text-slate-400 space-y-2">
            <span className="text-3xl">⚙️</span>
            <p className="text-sm font-bold text-slate-700">No products match your search</p>
            <p className="text-xs text-slate-500">Try searching by partial code or material name.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs divide-y divide-slate-200">
              <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-5 py-3.5">Product Code</th>
                  <th className="px-4 py-3.5">Description</th>
                  <th className="px-4 py-3.5">Castor Type</th>
                  <th className="px-4 py-3.5">Material</th>
                  <th className="px-4 py-3.5">Size</th>
                  <th className="px-4 py-3.5 text-right">Base Price (₹)</th>
                  <th className="px-4 py-3.5 text-center">Status</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3.5 font-mono font-bold text-slate-900 text-xs">
                      {product.code}
                    </td>
                    <td className="px-4 py-3.5 text-slate-700 font-medium max-w-[220px] truncate">
                      {product.description || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600">
                      {[product.castor_type, product.wheel_type].filter(Boolean).join(' / ') || (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 font-medium">
                      {product.wheel_material || <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-slate-600">{product.size || <span className="text-slate-300">-</span>}</td>
                    <td className="px-4 py-3.5 text-right font-extrabold text-slate-900 text-xs">
                      {product.base_price ? `₹${product.base_price.toFixed(2)}` : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                          product.is_active
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-red-50 text-red-800 border-red-200'
                        }`}
                      >
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap space-x-1.5">
                      <button
                        onClick={() => editProduct(product)}
                        className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-xs transition-colors"
                      >
                        Edit ✏️
                      </button>
                      <button
                        onClick={() => product.id && deleteProduct(product.id)}
                        className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-lg text-xs transition-colors"
                      >
                        Delete 🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
